import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ShoppingBag, Menu, X } from "lucide-react";
import { useTheme } from "styled-components";
import SettingsDropdown from "../SettingsDropdown";
import useLockBodyScroll from "../../hooks/useLockBodyScroll";
import Logo777 from "../../assets/icons/Logo777";
import {
  Nav,
  Inner,
  LogoLink,
  NavLinks,
  NavRouterLink,
  CartLink,
  CartBadge,
  DropdownWrap,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  ChevronIcon,
  SettingsWrap,
  MobileRight,
  MobileCartLink,
  MobileCartBadge,
  HamburgerButton,
  DrawerClose,
  MobileOverlay,
  MobileMenu,
  MobileNavLinks,
  MobileNavRouterLink,
} from "./Navbar.styles";

const INFO_ROUTES = [
  "/about",
  "/gallery",
  "/contact",
  "/size-guide",
  "/track-order",
];

/* ── Desktop dropdown ── */
function InfoDropdown() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const closeTimeout = useRef(null);
  const { pathname } = useLocation();

  const isInfoActive = INFO_ROUTES.some((r) => pathname.startsWith(r));

  const handleMouseEnter = () => {
    clearTimeout(closeTimeout.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      clearTimeout(closeTimeout.current);
    };
  }, []);

  return (
    <DropdownWrap
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DropdownTrigger
        $active={isInfoActive}
        onClick={() => setOpen((p) => !p)}
        type="button"
      >
        Info
        <ChevronIcon size={14} $open={open} />
      </DropdownTrigger>
      <DropdownMenu $open={open}>
        <DropdownItem to="/about" onClick={() => setOpen(false)}>
          About
        </DropdownItem>
        <DropdownItem to="/gallery" onClick={() => setOpen(false)}>
          Gallery
        </DropdownItem>
        <DropdownItem to="/contact" onClick={() => setOpen(false)}>
          Contact
        </DropdownItem>
        <DropdownItem to="/size-guide" onClick={() => setOpen(false)}>
          Size Guide
        </DropdownItem>
        <DropdownItem to="/track-order" onClick={() => setOpen(false)}>
          Track Order
        </DropdownItem>
      </DropdownMenu>
    </DropdownWrap>
  );
}

/* ── Main Navbar ── */
export default function Navbar({ scrolled, promoHeight = 0 }) {
  const location = useLocation();
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  useLockBodyScroll(mobileOpen);

  const cartCount = useSelector((s) =>
    s.cart.items.reduce((sum, item) => sum + item.quantity, 0),
  );

  const closeMobile = () => setMobileOpen(false);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobile();
  }, [location.pathname]);

  return (
    <>
      <Nav $promoHeight={promoHeight}>
        <Inner $scrolled={scrolled}>
          <LogoLink to="/">
            <Logo777 color={theme.light ? "#111111" : "#ffffff"} />
          </LogoLink>

          {/* ── Desktop links ── */}
          <NavLinks>
            <NavRouterLink to="/">Home</NavRouterLink>
            <NavRouterLink to="/shop">Shop</NavRouterLink>
            <NavRouterLink to="/atelier">Atelier</NavRouterLink>
            <InfoDropdown />
            <SettingsWrap>
              <SettingsDropdown />
            </SettingsWrap>
            <CartLink to="/cart" aria-label="Cart">
              <ShoppingBag size={18} />
              {cartCount > 0 && <CartBadge>{cartCount}</CartBadge>}
            </CartLink>
          </NavLinks>

          {/* ── Mobile right side: cart + hamburger ── */}
          <MobileRight>
            <MobileCartLink to="/cart" aria-label="Cart" $scrolled={scrolled}>
              <ShoppingBag size={18} strokeWidth={2.2} />
              {cartCount > 0 && <MobileCartBadge>{cartCount}</MobileCartBadge>}
            </MobileCartLink>

            <HamburgerButton
              aria-label="Open menu"
              onClick={() => setMobileOpen(true)}
              $scrolled={scrolled}
            >
              <Menu size={22} strokeWidth={2.4} />
            </HamburgerButton>
          </MobileRight>
        </Inner>
      </Nav>

      {/* ── Mobile overlay + drawer ── */}
      <MobileOverlay $open={mobileOpen} onClick={closeMobile} />

      {/* ── Drawer close button - fixed at same spot as hamburger ── */}
      {mobileOpen && (
        <DrawerClose aria-label="Close menu" onClick={closeMobile}>
          <X size={22} />
        </DrawerClose>
      )}

      <MobileMenu $open={mobileOpen}>
        <MobileNavLinks>
          <MobileNavRouterLink to="/" onClick={closeMobile}>
            Home
          </MobileNavRouterLink>
          <MobileNavRouterLink to="/shop" onClick={closeMobile}>
            Shop
          </MobileNavRouterLink>
          <MobileNavRouterLink to="/atelier" onClick={closeMobile}>
            Atelier
          </MobileNavRouterLink>
          <MobileNavRouterLink to="/about" onClick={closeMobile}>
            About
          </MobileNavRouterLink>
          <MobileNavRouterLink to="/gallery" onClick={closeMobile}>
            Gallery
          </MobileNavRouterLink>
          <MobileNavRouterLink to="/contact" onClick={closeMobile}>
            Contact
          </MobileNavRouterLink>
          <MobileNavRouterLink to="/size-guide" onClick={closeMobile}>
            Size Guide
          </MobileNavRouterLink>
          <MobileNavRouterLink to="/track-order" onClick={closeMobile}>
            Track Order
          </MobileNavRouterLink>
          <SettingsDropdown mobile />
        </MobileNavLinks>
      </MobileMenu>
    </>
  );
}
